import React, { FC, useEffect, useMemo, useState } from "react"
import AppButton from '../../../../components/AppButton'
import Typography from "../../../../components/Typography"
import { ServiceSelector } from "./ServiceSelector"
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap"
import { DateTimeSelector } from "./DateTimeSelector"
import { ProfessionalSelector } from "./ProfessionalSelector"
import { useTranslations } from "../../../../store/translations/hooks"
import { TK } from "../../../../store/translations/translationKeys"
import { useDispatch, useSelector } from "react-redux"
import { AnonymousScheduleRequest, Availability, CompanyPublicData, ProfessionalData, SelfScheduleRequest } from "../../../../models"
import { getProfessionalAvailabilityAsync, getProfessionalsPublicDataByIdAsync, postCompanyAppointmentsAsync, submitSelfScheduleAsync } from "../../../../ajax/requests"
import { alertError, alertSuccess } from "../../../../store/alerts/actions"
import { isUnauthorizedError } from "../../../../ajax/errors"
import moment from "moment"
import { getMyUser } from "../../../../store/session/selectors"
import { NewClientData, NewClientForm } from "./NewClientForm"
import { SuccessAnimation } from "./SuccessAnimation"
import TurnstileWidget from "../../../../components/TurnstileWidget"

export const PrettyScheduler: FC<{
  company: CompanyPublicData
  onDone?: (appointmentId: string) => void
}> = ({
  company,
  onDone,
}) => {
  const t = useTranslations()

  const dispatch = useDispatch()

  const user = useSelector(getMyUser)
  const isLoggedInAsClient = user?.isClient

  const [data, setData] = useState<Partial<SelfScheduleRequest>>({companyId: company?.id})
  const [loading, setLoading] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [availability, setAvailability] = useState<Availability[]>([])
  const [professionals, setProfessionals] = useState<Record<string, ProfessionalData>>({})
  const [newClient, setNewClient] = useState<NewClientData>({})
  const [newClientInvalidFields, setNewClientInvalidFields] = useState<(keyof NewClientData)[]>([])

  useEffect(() => setData({companyId: company?.id}), [company?.id])

  const professionalsToShow = useMemo(() => {
      if (!data.appointmentType || !data.dateTime) return []
      const ids = availability.filter(a => data.dateTime && a.dateTime.startsWith(data.dateTime.substring(0, 16))).map(a => a.professionalId)
      return [...new Set(ids)].map(id => professionals[id] ?? {id, name: id})
  }, [data.dateTime, professionals])

  useEffect(() => {
      if (!data.appointmentType) return;
      const missingProfessionals = [...new Set(availability.map(a => a.professionalId))].filter(id => !professionals[id])

      if (missingProfessionals?.length) {
        getProfessionalsPublicDataByIdAsync(missingProfessionals)
          .then(values => setProfessionals(prev => ({...prev, ...Object.fromEntries(values.map(v => ([v.id, v])))})))
      }
  }, [availability])

  const onAppointmentTypeSelected = (appointmentType: string) => {
      if (loading || !company?.clientsLogin?.selfScheduling) return

      setData(prev => ({...prev, appointmentType, dateTime: undefined, professionalId: undefined, room: undefined}))

      setLoading(true)
      const professionalIds = company.clientsLogin.selfScheduling.professionalsPerAppointmentType[appointmentType] ?? []
      const durationMin = company.services?.[appointmentType]?.defaultDurationMinutes ?? 60
      Promise.allSettled(professionalIds.map(professionalId =>
          getProfessionalAvailabilityAsync(professionalId, appointmentType, durationMin))
      )
      .then(results => setAvailability(results.flatMap(result => result.status === 'fulfilled' ? result.value : [])))
      .finally(() => setLoading(false))
  }

  const handleSchedule = () => {
      if (submitting || !data.companyId || !data.appointmentType || !data.professionalId || !data.dateTime) return;

      if (!isLoggedInAsClient) {
        const fieldsMissingAll: ((keyof NewClientData) | undefined)[] = [
            newClient.name?.trim().length ? undefined : 'name',
            newClient.email?.trim().length && /^[^@]+@[^@]+\.[^@]+$/u.test(newClient.email) ? undefined : 'email',
            newClient.acceptConsents ? undefined : 'acceptConsents',
            newClient.phone?.trim().length && /^\+{0,1}\d{7,16}$/u.test(newClient.phone) ? undefined : 'phone',
        ]

        const fieldsMissing = fieldsMissingAll
            .filter((v: (keyof NewClientData) | undefined): v is (keyof NewClientData) => !!v)

        if (fieldsMissing.length) {
            setNewClientInvalidFields(fieldsMissing)
            return
        }

        const anonymousRequest: AnonymousScheduleRequest = {
            ...(data as SelfScheduleRequest),
            clientName: newClient.name!,
            clientEmail: newClient.email!,
            clientPhone: newClient.phone!,
            notes: newClient.notes,
            acceptPrivacyPolicy: newClient.acceptConsents!,
        }

        setSubmitting(true)
        postCompanyAppointmentsAsync(company.id, anonymousRequest)
            .then(appointmentId => {
                setFinished(true)
                setData({companyId: company?.id})
                onDone && onDone(appointmentId)
            })
            .catch(error => {
                console.error(error)
                if(isUnauthorizedError(error)) return false
                dispatch(alertError((error as {data?:{message?:string}}).data?.message ?? t(TK.somethingWentWrong)))
            })
            .finally(() => setSubmitting(false))
      } else {
        setSubmitting(true)
        submitSelfScheduleAsync(data as SelfScheduleRequest)
            .then(appointmentId => {
                setFinished(true)
                setData({companyId: company?.id})
                onDone && onDone(appointmentId)
            })
            .catch(error => {
                console.error(error)
                if(isUnauthorizedError(error)) return false
                dispatch(alertError((error as {data?:{message?:string}}).data?.message ?? t(TK.somethingWentWrong)))
            })
            .finally(() => setSubmitting(false))
        }
  }

  const professional = useMemo(
      () => data.professionalId && professionals[data.professionalId] || undefined,
      [data.professionalId, professionals],
  )

  return (<>
    { finished
        ?
            <div style={{margin: 'var(--spacer-L)', gap: 'var(--spacer-M)', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <SuccessAnimation />
                <Typography size="x-large">{t(TK.submittedSuccessfully)}!</Typography>
                <Typography>{t(TK.verifyYourEmailInbox)}</Typography>
                <AppButton style={{marginTop: 'var(--spacer-M)'}} outline onClick={() => setFinished(false)}>{t(TK.goBack)}</AppButton>
            </div>
        :   <ServiceSelector
                settings={company}
                value={data.appointmentType}
                onUpdate={onAppointmentTypeSelected}
            />
    }

    {loading && <div style={{width: '100%', height: '100%', paddingTop: 'var(--spacer-M)', textAlign: 'center', position: 'absolute', top: 0, left: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)'}}><Spinner/></div>}

    <Modal size="lg" isOpen={!loading && !!data.appointmentType}>
        <ModalHeader toggle={() => setData({companyId: company?.id})}>
            <Typography size='x-large'>{t(TK.date)}</Typography>
        </ModalHeader>
        <ModalBody>
            <DateTimeSelector
                options={availability}
                value={data.dateTime}
                onUpdate={dateTime => setData(d => ({...d, dateTime, professionalId: undefined, room: undefined}))}
            />

            {!!data.appointmentType && !!data.dateTime && <>
                <hr style={{margin: 'var(--spacer-S) 0'}}/>
                <Typography size='x-large'>{t(TK.professional)}</Typography>
                <ProfessionalSelector
                    options={professionalsToShow ?? []}
                    value={professional}
                    onUpdate={(selected) => setData(prev => ({
                        ...prev,
                        professionalId: selected?.id,
                        room: selected
                            ? availability.find(a => a.professionalId === selected.id && a.dateTime.substring(0, 16) === prev.dateTime?.substring(0, 16))?.room
                            : undefined,
                    }))}
                />
            </>}

            {!!data?.appointmentType && !!data.professionalId && !!data.dateTime && !isLoggedInAsClient && <>
                <hr style={{margin: 'var(--spacer-S) 0'}}/>
                <Typography size='x-large' block style={{marginBottom: 'var(--spacer-S)'}}>{t(TK.personalData)}</Typography>
                <NewClientForm
                    data={newClient}
                    onChange={d => {setNewClient(d); setNewClientInvalidFields([])}}
                    invalidFields={newClientInvalidFields}
                    companyPolicyUrl={company?.clientsLogin?.selfScheduling?.privacyPolicyUrl}
                />
            </>}

            {!!data?.appointmentType && !!data.professionalId && !!data.dateTime && <>
                <hr style={{margin: 'var(--spacer-S) 0'}}/>
                <TurnstileWidget />
                <AppButton
                    color='success'
                    style={{width: '100%', padding: 'var(--spacer-S)'}}
                    onClick={handleSchedule}
                >
                    <Typography size='large'>
                        {t(TK.schedule)}
                        &nbsp;
                        {data.appointmentType}
                        &nbsp;({moment(data.dateTime).format('ddd D MMMM - H:mm')})
                        &nbsp;&nbsp;
                        {submitting && <Spinner size='sm' color="secundary"/>}
                    </Typography>
                </AppButton>
            </>}
        </ModalBody>
    </Modal>

  </>)
}
